import clamp from 'lodash/clamp';
import merge from 'lodash/merge';
import {useMemo} from 'react';
import {twMerge} from '../../../common/twMerge';
import {
  MOBILE_VIEWER_SEEKBAR_HEIGHT_PX,
  ViewerType,
} from '../../../domain/content/[seriesId]/viewer/[productId]/constant/viewerConstant';
import {putTiaraObjectProps} from '../../../logging/putTiaraObjectProps';

import {Icon} from '../../atoms/Icon/Icon';
import {IconType} from '../../atoms/Icon/IconType.enum';
import {Text} from '../../atoms/Text/Text';
import {Slider} from '../../molecules/Slider/Slider';
import {MobileViewerSeekbarProps} from './MobileViewerSeekbarProps.interface';

/**
 * 실제 기능 구현 시 이벤트 핸들러가 추가로 필요하다면 추가해야 합니다.
 * Progress는 0~1의 값을 가집니다.
 */
export function MobileViewerSeekbar({
  viewerType,
  currentPage,
  // TODO: load 페이지 에 대한 논의 필요
  loadedPage,
  totalPage,
  currentProgress,
  loadedProgress,
  currentTime,
  totalTime,
  onChangeProgress,
  onPressProgressBar,
  onReleaseProgressBar,
  onClickFullscreenBtn,
  className,
  style,
  isShortsDrama,
  isSeeking,
  isVideoVisible,
  ...props
}: MobileViewerSeekbarProps) {
  const countTextWidth = useMemo(() => {
    if (totalPage) {
      return convertDigitToWidthPxSize(totalPage.toString().length);
    }
    if (totalTime) {
      return convertDigitToWidthPxSize(totalTime.toString().length);
    }
    return convertDigitToWidthPxSize(1);
  }, [totalPage, totalTime]);

  const isVideoViewer = viewerType === ViewerType.VideoViewer;

  return (
    <div
      className={twMerge(
        'flex w-full touch-none flex-row items-center justify-between bg-theme-bg-base pr-15pxr',
        isVideoViewer && 'bg-transparent pl-15pxr',
        !isVideoViewer && 'border-t border-solid border-theme-transparent-10',
        isShortsDrama && isVideoVisible && 'hidden',
        className
      )}
      style={merge(
        {
          height: MOBILE_VIEWER_SEEKBAR_HEIGHT_PX,
        },
        style
      )}
      {...props}
    >
      {isVideoViewer && !isShortsDrama && (
        <Text
          className="font-small2-bold select-none text-right text-static-mono-10"
          style={{width: `${countTextWidth}px`}}
        >
          {currentTime}
        </Text>
      )}
      {isShortsDrama && isSeeking && (
        <div className="absolute h-24pxr mb-60pxr inset-x-0 flex justify-center">
          <div className="bg-static-mono-black px-8pxr rounded-20pxr">
            <Text
              className="font-small2-bold text-static-mono-white"
              style={{width: `${countTextWidth}px`}}
            >
              {currentTime}
            </Text>
          </div>
        </div>
      )}
      <Slider
        isMobile={true}
        loadedValue={loadedProgress}
        currentValue={currentProgress}
        hasIndicator={true}
        onPressSlider={onPressProgressBar}
        onReleaseSlider={onReleaseProgressBar}
        onValueChange={onChangeProgress}
        className={`ml-18pxr mr-16pxr mt-6pxr mb-1px px-6pxr ${
          isVideoViewer && 'my-0 ml-16pxr'
        }`}
        barStyleClassName={
          isVideoViewer
            ? 'bg-[rgb(var(--colors-static-mono-white)_/_10%)]'
            : undefined
        }
        valueStyleClassName={isVideoViewer ? 'bg-static-mono-white' : undefined}
        loadStyleClassName={
          isVideoViewer
            ? 'bg-[rgb(var(--colors-static-mono-white)_/_20%)]'
            : undefined
        }
        indicatorStyleClassName={
          isVideoViewer ? 'bg-static-mono-white' : undefined
        }
      />
      {viewerType === ViewerType.TalkViewer && (
        <div className="flex w-32pxr select-none flex-row items-center">
          <Text className="font-small2-bold text-theme-solid-100">
            {Math.round(clamp(currentProgress * 100, 0, 100))}%
          </Text>
        </div>
      )}
      {/* 텍스트뷰어나 이미지뷰어는 설계상 로딩중이거나 값이 없으면 0, 이외의 상황에서는 1 ~ count 입니다. */}
      {(viewerType === ViewerType.TextViewer ||
        viewerType === ViewerType.ImageViewer) && (
        <div className="flex select-none flex-row pt-7pxr pb-2pxr">
          <Text
            className="font-small2-bold text-right text-theme-solid-100"
            style={{width: `${countTextWidth}px`}}
          >
            {totalPage && currentPage ? currentPage : '-'}
          </Text>
          <Text className="font-small2 px-2pxr text-theme-solid-40">/</Text>
          <Text
            className="font-small2 text-right text-theme-solid-40"
            style={{width: `${countTextWidth}px`}}
          >
            {totalPage ? totalPage : '-'}
          </Text>
        </div>
      )}
      {isVideoViewer && !isShortsDrama && (
        <>
          <Text
            className="font-small2 select-none text-right text-static-mono-40"
            style={{width: `${countTextWidth}px`}}
          >
            {totalTime}
          </Text>
          {onClickFullscreenBtn && (
            <Icon
              type={IconType.ViewerSeekbarExpandStatic}
              onClick={onClickFullscreenBtn}
              className="ml-8pxr cursor-pointer"
              {...putTiaraObjectProps({
                click: {
                  layer2: '시크바',
                },
              })}
            />
          )}
        </>
      )}
    </div>
  );
}

function convertDigitToWidthPxSize(digit: number) {
  return digit * 8;
}
